import { useQuery } from 'react-query'
import { useAuth0 } from '../../../contexts/auth0-context'
import callAPI from '../../../API/callSecureAPI'
import endpoints from '../../../API/endpoints'
import { Tpp, TppColumn, TppSection, TppRow, TppSlot, TppCell, TppMatrixState } from '../types'

const buildLookup = <T extends { _id: string }>(items: T[]) =>
    items.reduce((acc, item) => ({ ...acc, [item._id]: item }), {} as Record<string, T>)

const buildCellLookup = (cells: TppCell[]) => {
    const lookup: Record<string, Record<string, TppCell>> = {}
    cells.forEach((cell) => {
        if (!lookup[cell.slotId]) lookup[cell.slotId] = {}
        lookup[cell.slotId][cell.columnId] = cell
    })
    return lookup
}

export const getTppCellsQueryKey = (tppId?: string | null) => (tppId ? ['tppCells', tppId] : ['tppCells'])

export const useTppData = (tppId: string | null | undefined) => {
    const { getTokenSilently, isAuthenticated, isLoading: authLoading } = useAuth0()

    const fetchFromEndpoint = async <T>(endpoint: { url: string; method: string }, path: string): Promise<T> => {
        const token = await getTokenSilently({ audience: 'https://atlas.aesara.com' })
        const response = await callAPI(`${endpoint.url}/${path}`, endpoint.method, token)
        return response.data as T
    }

    const canFetch = !!tppId && !!getTokenSilently && !!isAuthenticated && !authLoading
    const tppQuery = useQuery<Tpp>(['tpp', tppId], () => fetchFromEndpoint(endpoints.getTPP, tppId), {
        enabled: canFetch,
    })
    const columnsQuery = useQuery<TppColumn[]>(
        ['tppColumns', tppId],
        () => fetchFromEndpoint(endpoints.getTPPColumns, tppId),
        { enabled: canFetch }
    )
    const sectionsQuery = useQuery<TppSection[]>(
        ['tppSections', tppId],
        () => fetchFromEndpoint(endpoints.getTPPSections, tppId),
        { enabled: canFetch }
    )
    const rowsQuery = useQuery<TppRow[]>(['tppRows', tppId], () => fetchFromEndpoint(endpoints.getTPPRows, tppId), {
        enabled: canFetch,
    })
    const slotsQuery = useQuery<TppSlot[]>(['tppSlots', tppId], () => fetchFromEndpoint(endpoints.getTPPSlots, tppId), {
        enabled: canFetch,
    })
    const cellsQuery = useQuery<TppCell[]>(
        getTppCellsQueryKey(tppId),
        () => fetchFromEndpoint(endpoints.getTPPCellsByTPPId, tppId),
        {
            enabled: canFetch,
        }
    )

    const isLoading =
        tppQuery.isLoading ||
        columnsQuery.isLoading ||
        sectionsQuery.isLoading ||
        rowsQuery.isLoading ||
        slotsQuery.isLoading ||
        cellsQuery.isLoading

    const error =
        tppQuery.error ||
        columnsQuery.error ||
        sectionsQuery.error ||
        rowsQuery.error ||
        slotsQuery.error ||
        cellsQuery.error

    let matrixState: TppMatrixState | null = null

    if (
        !isLoading &&
        tppQuery.data &&
        columnsQuery.data &&
        sectionsQuery.data &&
        rowsQuery.data &&
        slotsQuery.data &&
        cellsQuery.data
    ) {
        matrixState = {
            tpp: tppQuery.data,
            columns: buildLookup(columnsQuery.data),
            sections: buildLookup(sectionsQuery.data),
            rows: buildLookup(rowsQuery.data),
            slots: buildLookup(slotsQuery.data),
            cellLookup: buildCellLookup(cellsQuery.data),
            pinnedColumnIds: columnsQuery.data.filter((c) => c.isPinned).map((c) => c._id),
            isLoading: false,
        }
    }

    return {
        matrixState,
        isLoading,
        error,
    }
}
